import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { createCampaign, updateCampaign, uploadCampaignAssets, uploadCampaignCoverImage } from './service'
import type { CreateCampaignPayload, UpdateCampaignPayload } from './types'

// Create Campaign with no budget
export const useCreateCampaignMutation = () => {
  return useMutation({
    mutationFn: (campaign: CreateCampaignPayload) => createCampaign(campaign),
    onError: () => {
      toast.error('Failed to create campaign')
    },
  })
}

// Upload Campaign Cover Image - mandatory
export const useUploadCampaignCoverImageMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      campaignId,
      imageFile,
    }: {
      campaignId: string
      imageFile: File
    }) => uploadCampaignCoverImage(campaignId, imageFile),
    onSuccess: () => {
      // Invalidate all infinite campaigns queries
      queryClient.invalidateQueries({
        queryKey: ['campaigns-infinite'],
      })
    },
    onError: () => {
      toast.error('Failed to upload campaign cover image')
    },
  })
}

// Upload Campaign Assets - optional
export const useUploadCampaignAssetsMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      campaignId,
      files,
    }: {
      campaignId: string
      files: File[]
    }) => uploadCampaignAssets(campaignId, files),
    onSuccess: () => {
      // Invalidate all infinite campaigns queries
      queryClient.invalidateQueries({
        queryKey: ['campaigns-infinite'],
      })
    },
    onError: () => {
      toast.error('Failed to upload campaign assets')
    },
  })
}

// Update Campaign budget - launch campaign
export const useUpdateCampaignMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (campaign: UpdateCampaignPayload) => updateCampaign(campaign),
    onError: () => {
      toast.error('Failed to update campaign')
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns-infinite'] })
      if (variables?.id) {
        queryClient.invalidateQueries({ queryKey: ['campaign', String(variables.id)] })
      }
      toast.success('Campaign Created successfully')
    },
  })
}
